import React, { createRef, useCallback, useEffect, useMemo, useState } from 'react';
import useOfflineSupplierColumns from 'src/assets/static/new_receipt/useOfflineSupplierColumns';
import { IOfflineSupplier } from 'src/types/OfflineSupplier';

import { State, process } from '@progress/kendo-data-query';
import {
  Grid,
  GridDataStateChangeEvent,
  GridHeaderSelectionChangeEvent,
  GridPagerSettings,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid';

import { GridSelectableSettings } from '@progress/kendo-react-grid/dist/npm/interfaces/GridSelectableSettings';
import uuid from 'react-uuid';
import { useAppDispatch } from 'src/app/hooks';
import GridWrapper from 'src/components/GridWrapper';
import SearchInput from 'src/components/SearchInput';
import { fetchSupplierGridData } from 'src/features/POGridSlice';
import CreateReceiptToolBar from './enter_receipt_wizard/CreateReceiptToolBar';

const gridPagerSettings: GridPagerSettings = {
  buttonCount: 5,
  pageSizes: [10, 20, 50, 100],
};
const initialDataState: State = {
  skip: 0,
  take: 20,
};

const selectable: GridSelectableSettings = {
  enabled: true,
  drag: false,
  cell: true,
  mode: 'single',
};

const defaultProps = {
  tableTextId: 'Suppliers',
  DataItemKey: 'rowId',
  showColumnMenu: true,
  sortable: true,
  resizable: true,
  selectable: selectable,
  GridPagerSettings: gridPagerSettings,
} as const;

interface IProps {
  setSelectedSupplier: (data: IOfflineSupplier) => void;
}

const OfflineSupplierGrid: React.FC<IProps> = ({ setSelectedSupplier }) => {
  const tableRef = createRef<Grid>();

  const [offlineSupplierDetails, setOfflineSupplierDetails] = useState<IOfflineSupplier[]>([]);

  const columns = useOfflineSupplierColumns(tableRef);

  const [dataState, setDataState] = useState(initialDataState);
  const [isOfflineSupplierLoading, setIsOfflineSupplierLoading] = useState(false);

  const [searchSupplier, setSearchSupplier] = useState('');

  const onSupplierDataStateChange = (e: GridDataStateChangeEvent) => {
    setDataState(e.dataState);
  };
  const dispatch = useAppDispatch();

  const getOfflineSupplier = useCallback(() => {
    setIsOfflineSupplierLoading(true);
    dispatch(fetchSupplierGridData())
      .unwrap()
      .then((res) => {
        const response = res?.map((item: IOfflineSupplier[]) => ({
          ...item,
          rowId: uuid(),
          selected: false,
        }));
        setOfflineSupplierDetails(response);
      })
      .finally(() => {
        setIsOfflineSupplierLoading(false);
      });
  }, [dispatch, setOfflineSupplierDetails]);

  const onSelectionChange = useCallback(
    (event: GridSelectionChangeEvent) => {
      setOfflineSupplierDetails((prev) => {
        const prevItems = [...prev];
        const index = event?.dataItem?.rowId;
        return prevItems?.map((el) => ({ ...el, selected: el?.rowId === index }));
      });
    },
    [setOfflineSupplierDetails]
  );

  const handleSupplierSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const data = e.target.value;
    setSearchSupplier(data);
  };

  // clear filter
  const handleClearFilter = useCallback(() => {
    setDataState(initialDataState);
    setSearchSupplier('');
  }, []);
  const handleClearSelection = useCallback(() => {
    setOfflineSupplierDetails((prev: any) => {
      const selectedPOList = prev.map((item: any) => ({
        ...item,
        selected: false,
      }));
      return selectedPOList;
    });
  }, []);

  // refresh supplier grid
  const handleRefresh = useCallback(() => {
    setSearchSupplier('');
    setSelectedSupplier({} as IOfflineSupplier);
    getOfflineSupplier();
  }, [getOfflineSupplier, setSelectedSupplier]);

  const isClearFiltersDisabled = useMemo(
    () => !dataState.filter?.filters.length,
    [dataState.filter?.filters.length]
  );

  const gridToolbarChildren = useMemo(
    () => (
      <CreateReceiptToolBar
        handleRefresh={handleRefresh}
        clearFilter={handleClearFilter}
        clearSelection={handleClearSelection}
        isLoading={isOfflineSupplierLoading}
        isClearFiltersDisabled={isClearFiltersDisabled}
      />
    ),
    [
      handleClearFilter,
      handleClearSelection,
      handleRefresh,
      isClearFiltersDisabled,
      isOfflineSupplierLoading,
    ]
  );

  const filteredData = useMemo(
    () =>
      offlineSupplierDetails?.filter((item) =>
        Object.values(item).join('').toLowerCase()?.includes(searchSupplier?.toLowerCase())
      ),
    [offlineSupplierDetails, searchSupplier]
  );
  const { data, total } = useMemo(
    () =>
      process(filteredData || [], dataState) as {
        data: IOfflineSupplier[];
        total: number;
      },
    [dataState, filteredData]
  );

  const onHeaderSelectionChange = useCallback(
    (event: GridHeaderSelectionChangeEvent) => {
      const checkboxElement: any = event.syntheticEvent.target;
      const { checked } = checkboxElement;
      setOfflineSupplierDetails((prev) =>
        prev.map((element) => ({ ...element, selected: checked }))
      );
    },
    [setOfflineSupplierDetails]
  );

  const selectedSupplierRows = useMemo(
    () => data.find((item) => item.selected) || ({} as IOfflineSupplier),
    [data]
  );

  useEffect(() => {
    getOfflineSupplier();
  }, [getOfflineSupplier]);

  useEffect(() => {
    setSelectedSupplier?.(selectedSupplierRows);
  }, [selectedSupplierRows, setSelectedSupplier]);

  return (
    <div>
      <div className="mb-1">
        <p className="text-sm font-bold mb-1">Filter Suppliers by Name</p>
        <SearchInput
          width="100%"
          value={searchSupplier}
          placeholder="Enter supplier name (Optional)"
          onChange={handleSupplierSearch}
        />
      </div>
      <GridWrapper
        columns={columns}
        total={total || 0}
        dataSource={data || []}
        dataState={dataState}
        loading={isOfflineSupplierLoading}
        onHeaderSelectionChange={onHeaderSelectionChange}
        height="45vh"
        onDataStateChange={onSupplierDataStateChange}
        onSelectionChange={onSelectionChange}
        gridToolbarChildren={gridToolbarChildren}
        pageable={gridPagerSettings}
        {...defaultProps}
      />
    </div>
  );
};

export default OfflineSupplierGrid;
